import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';
import { ElementRenderer } from '@/pages/player/elements';

const optionKey = (o, i) => String(o?.value ?? o?.id ?? i);

// Grade a quiz in the browser for builder previews (nothing is recorded)
const gradeLocally = (quiz, answers) => {
  let earned = 0;
  let total = 0;
  const out = {};
  (quiz.questions || []).forEach((q) => {
    const pts = q.points || 1;
    total += pts;
    const a = answers[q.id];
    const opts = q.options || [];
    let correct = false;
    let correctAnswer = q.correct_answer;
    if (q.type === 'multiple_choice') {
      const right = (q.correct_answers?.length ? q.correct_answers : opts.map((o, i) => (o.is_correct || o.isCorrect) ? optionKey(o, i) : null).filter(Boolean)).map(String);
      correct = Array.isArray(a) && a.length === right.length && a.map(String).every(x => right.includes(x));
      correctAnswer = right;
    } else if (q.type === 'true_false' && !opts.length) {
      correct = String(a).toLowerCase() === String(q.correct_answer).toLowerCase();
      correctAnswer = String(q.correct_answer).toLowerCase();
    } else if (opts.length) {
      const idx = opts.findIndex(o => o.is_correct || o.isCorrect);
      correctAnswer = idx >= 0 ? optionKey(opts[idx], idx) : q.correct_answer;
      correct = a !== undefined && String(a).toLowerCase() === String(correctAnswer).toLowerCase();
    } else {
      correct = String(a || '').trim().toLowerCase() === String(q.correct_answer || '').trim().toLowerCase();
    }
    if (correct) earned += pts;
    out[q.id] = { correct, correct_answer: correctAnswer, explanation: q.explanation, points: correct ? pts : 0 };
  });
  const score = total ? Math.round((earned / total) * 100) : 0;
  return { score, passed: score >= (quiz.passing_score || 70), answers: out, earned_points: earned, total_points: total };
};

// Try a single element exactly as a learner sees it, without publishing or recording progress
export const ElementPreviewDialog = ({ element, onClose }) => {
  const [runKey, setRunKey] = useState(0);
  const [done, setDone] = useState(false);
  if (!element) return null;
  const quiz = element.config?.quiz || {};
  const fakeProgress = { completed_element_ids: done ? [element.id] : [], element_progress: {}, quiz_submissions: [] };

  return (
    <Dialog open={!!element} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="element-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview: {element.title}</DialogTitle>
          <DialogDescription>This is exactly what learners see. Nothing is saved or recorded.</DialogDescription>
        </DialogHeader>
        <div key={runKey} className="pt-2">
          <ElementRenderer
            element={element}
            progress={fakeProgress}
            isCompleted={false}
            onComplete={() => setDone(true)}
            onSaveData={() => {}}
            onSubmitQuiz={async (answers) => gradeLocally(quiz, answers)}
            onModuleRestart={() => {}}
            currentModuleIdx={0}
          />
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-xs text-slate-500" data-testid="preview-completion-state">{done ? 'Learner would complete this element ✓' : 'Not yet completed'}</span>
          <Button variant="outline" size="sm" onClick={() => { setRunKey(k => k + 1); setDone(false); }} data-testid="preview-reset-btn">
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
